import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  ShieldCheckIcon,
  PencilIcon,
  UserIcon,
  StarIcon,
  ShieldExclamationIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  BoltIcon,
  DocumentDuplicateIcon,
  LinkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import { UserRole, UserProfile, RecentUpload, ApiProvider } from '../types';
import { useToast } from '../hooks/useToast';
import RoleGuard from '../components/RoleGuard';
import LoadingSpinner from '../components/LoadingSpinner';
import UserProfileModal from '../components/UserProfileModal';

interface UserStatistics {
  role: UserRole;
  user_count: number;
  total_scans: number;
  unique_providers_used: number;
  avg_monthly_activity: number;
}

interface SystemHealthStatus {
  component: string;
  status: 'healthy' | 'warning' | 'error';
  response_time?: number;
  message: string;
  last_checked: string;
}

type AdminTab = 'uploads' | 'health' | 'users' | 'pages';

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [statistics, setStatistics] = useState<UserStatistics[]>([]);
  const [recentUploads, setRecentUploads] = useState<RecentUpload[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealthStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('uploads');
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all users
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, name, email, role, created_at, subscription_end_date, last_login')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      setUsers(usersData || []);

      // Load statistics
      const { data: statsData, error: statsError } = await supabase
        .from('user_statistics')
        .select('*');

      if (statsError) throw statsError;
      setStatistics(statsData || []);

      // Load recent uploads
      await loadRecentUploads();

    } catch (error) {
      console.error('Error loading admin data:', error);
      showToast('error', 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentUploads = async () => {
    try {
      // Load recent scans as uploads with explicit user profile join
      const { data: scansData, error: scansError } = await supabase
        .from('scans')
        .select(`
          id,
          created_at,
          api_provider,
          image_url,
          title,
          user_id
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (scansError) throw scansError;

      if (!scansData || scansData.length === 0) {
        setRecentUploads([]);
        return;
      }

      // Get user IDs from scans
      const userIds = [...new Set(scansData.map(scan => scan.user_id))];
      
      // Fetch user profiles separately
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Create a map of user ID to profile
      const profileMap = new Map();
      (profilesData || []).forEach(profile => {
        profileMap.set(profile.id, profile);
      });

      const uploads: RecentUpload[] = scansData.map(scan => {
        const profile = profileMap.get(scan.user_id);
        return {
          id: scan.id,
          filename: scan.image_url ? scan.image_url.split('/').pop() || scan.title || 'Unknown' : scan.title || 'Unknown',
          uploader_name: profile?.name || 'Unknown User',
          uploader_id: scan.user_id,
          upload_time: scan.created_at,
          file_size: Math.floor(Math.random() * 5000000) + 100000, // Simulated file size
          status: 'complete', // All existing scans are complete since we don't track processing status yet
          api_provider: scan.api_provider
        };
      });

      setRecentUploads(uploads);
    } catch (error) {
      console.error('Error loading recent uploads:', error);
      showToast('error', 'Failed to load recent uploads');
    }
  };

  const performSystemHealthCheck = async () => {
    setHealthCheckLoading(true);
    const healthChecks: SystemHealthStatus[] = [];

    try {
      // 1. Database Connection Check
      const dbStart = Date.now();
      try {
        const { error: dbError } = await supabase.from('profiles').select('count').limit(1);
        const dbTime = Date.now() - dbStart;
        
        healthChecks.push({
          component: 'Supabase Database',
          status: dbError ? 'error' : dbTime > 1000 ? 'warning' : 'healthy',
          response_time: dbTime,
          message: dbError ? `Database error: ${dbError.message}` : `Connected (${dbTime}ms)`,
          last_checked: new Date().toISOString()
        });
      } catch (error) {
        healthChecks.push({
          component: 'Supabase Database',
          status: 'error',
          message: `Connection failed: ${error}`,
          last_checked: new Date().toISOString()
        });
      }

      // 2. Authentication Service Check
      const authStart = Date.now();
      try {
        const { error: authError } = await supabase.auth.getSession();
        const authTime = Date.now() - authStart;
        
        healthChecks.push({
          component: 'Authentication Service',
          status: authError ? 'error' : authTime > 500 ? 'warning' : 'healthy',
          response_time: authTime,
          message: authError ? `Auth error: ${authError.message}` : `Service active (${authTime}ms)`,
          last_checked: new Date().toISOString()
        });
      } catch (error) {
        healthChecks.push({
          component: 'Authentication Service',
          status: 'error',
          message: `Service unavailable: ${error}`,
          last_checked: new Date().toISOString()
        });
      }

      // 3. API Provider Checks (simulated - replace with actual API calls)
      const apiProviders = [
        { name: 'Gemini AI', endpoint: '/api/gemini' },
        { name: 'SerpAPI', endpoint: '/api/serpapi' },
        { name: 'SearchAPI', endpoint: '/api/searchapi' },
        { name: 'OpenLens', endpoint: '/api/openlens' },
        { name: 'Decodo', endpoint: '/api/decodo' }
      ];

      for (const provider of apiProviders) {
        // Simulate API health check
        const responseTime = Math.floor(Math.random() * 2000) + 100;
        const isHealthy = Math.random() > 0.1; // 90% success rate
        
        healthChecks.push({
          component: provider.name,
          status: !isHealthy ? 'error' : responseTime > 1000 ? 'warning' : 'healthy',
          response_time: responseTime,
          message: !isHealthy ? 'Service unavailable' : `API responsive (${responseTime}ms)`,
          last_checked: new Date().toISOString()
        });
      }

      // 4. Background Job Queue (simulated)
      const queueSize = Math.floor(Math.random() * 10);
      healthChecks.push({
        component: 'Background Job Queue',
        status: queueSize > 5 ? 'warning' : 'healthy',
        message: `${queueSize} pending tasks`,
        last_checked: new Date().toISOString()
      });

      // 5. Server Resources (simulated)
      const cpuUsage = Math.floor(Math.random() * 100);
      const memoryUsage = Math.floor(Math.random() * 100);
      
      healthChecks.push({
        component: 'Server Resources',
        status: cpuUsage > 80 || memoryUsage > 80 ? 'warning' : 'healthy',
        message: `CPU: ${cpuUsage}%, Memory: ${memoryUsage}%`,
        last_checked: new Date().toISOString()
      });

      setSystemHealth(healthChecks);
      showToast('success', 'System health check completed');
    } catch (error) {
      console.error('Error performing health check:', error);
      showToast('error', 'Failed to complete system health check');
    } finally {
      setHealthCheckLoading(false);
    }
  };

  const getStatusIcon = (status: 'healthy' | 'warning' | 'error' | 'processing' | 'complete' | 'failed') => {
    switch (status) {
      case 'healthy':
      case 'complete':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatLastLogin = (dateString?: string): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 24 * 7) return `${Math.floor(diffInHours / 24)} days ago`;
    return date.toLocaleDateString();
  };

  const confirmRoleChange = (userId: string, newRole: UserRole, userName: string) => {
    const confirmMessage = `Are you sure you want to change ${userName}'s role to ${newRole}? This action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      updateUserRole(userId, newRole);
    }
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));

      showToast('success', `User role updated to ${newRole}`);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user role:', error);
      showToast('error', 'Failed to update user role');
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'admin': return <ShieldExclamationIcon className="h-5 w-5 text-purple-600" />;
      case 'paid': return <StarIcon className="h-5 w-5 text-yellow-600" />;
      case 'user': return <UserIcon className="h-5 w-5 text-blue-600" />;
      default: return <UserIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'paid': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'user': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <RoleGuard allowedRoles={[UserRole.ADMIN]} fallback={
      <div className="text-center py-12">
        <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Access Denied
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          You need admin privileges to access this page.
        </p>
      </div>
    }>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Admin Panel
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage users, monitor uploads, and check system health
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('uploads')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'uploads'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <CloudArrowUpIcon className="h-5 w-5 inline-block mr-2" />
              Recent Uploads
            </button>
            <button
              onClick={() => setActiveTab('health')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'health'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <CpuChipIcon className="h-5 w-5 inline-block mr-2" />
              System Health
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <UserGroupIcon className="h-5 w-5 inline-block mr-2" />
              User Management
            </button>
            <button
              onClick={() => setActiveTab('pages')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pages'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <DocumentDuplicateIcon className="h-5 w-5 inline-block mr-2" />
              App Pages
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'uploads' && (
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                  Recent Uploads
                </h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Filename
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Uploader
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Upload Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        File Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Provider
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {recentUploads.map((upload) => (
                      <tr key={upload.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {upload.filename}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {upload.uploader_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              ID: {upload.uploader_id.substring(0, 8)}...
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(upload.upload_time).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatFileSize(upload.file_size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-full">
                            {upload.api_provider || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            upload.status === 'complete'
                              ? 'bg-green-100 text-green-800'
                              : upload.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {getStatusIcon(upload.status)}
                            <span className="ml-1 capitalize">{upload.status}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'health' && (
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <CpuChipIcon className="h-5 w-5 mr-2" />
                  System Health Check
                </h2>
                <button
                  onClick={performSystemHealthCheck}
                  disabled={healthCheckLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
                >
                  {healthCheckLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <BoltIcon className="h-4 w-4" />
                  )}
                  <span>{healthCheckLoading ? 'Running Check...' : 'Run Health Check'}</span>
                </button>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {systemHealth.map((component, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {component.component}
                        </h3>
                        {getStatusIcon(component.status)}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {component.message}
                      </p>
                      {component.response_time && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Response time: {component.response_time}ms
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Last checked: {new Date(component.last_checked).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                
                {systemHealth.length === 0 && (
                  <div className="text-center py-8">
                    <ServerIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Click "Run Health Check" to check system status
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statistics.map((stat) => (
                <div key={stat.role} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {getRoleIcon(stat.role)}
                      <h3 className="ml-2 font-semibold text-gray-900 dark:text-white capitalize">
                        {stat.role} Users
                      </h3>
                    </div>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stat.user_count}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex justify-between">
                      <span>Total Scans:</span>
                      <span>{stat.total_scans}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Providers Used:</span>
                      <span>{stat.unique_providers_used}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Users Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  User Management
                </h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Subscription
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((userProfile) => (
                      <tr key={userProfile.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {userProfile.name || 'No name'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {userProfile.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(userProfile.role)}`}>
                            {getRoleIcon(userProfile.role)}
                            <span className="ml-1 capitalize">{userProfile.role}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(userProfile.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatLastLogin(userProfile.last_login)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {userProfile.subscription_end_date ? (
                            <span className={`px-2 py-1 rounded text-xs ${
                              new Date(userProfile.subscription_end_date) > new Date() 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {new Date(userProfile.subscription_end_date).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {editingUser === userProfile.id ? (
                            <div className="flex space-x-2">
                              <select
                                className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded px-2 py-1"
                                defaultValue={userProfile.role}
                                onChange={(e) => confirmRoleChange(userProfile.id, e.target.value as UserRole, userProfile.name)}
                              >
                                <option value="guest">Guest</option>
                                <option value="user">User</option>
                                <option value="paid">Paid</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                onClick={() => setEditingUser(null)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex space-x-4">
                              <button
                                onClick={() => setEditingUser(userProfile.id)}
                                className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center"
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                Edit Role
                              </button>
                              <button
                                onClick={() => setSelectedUser(userProfile)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                View Profile
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pages Tab */}
        {activeTab === 'pages' && (
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
                  App Pages Directory
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  All available pages in the web application
                </p>
              </div>
              
              <div className="p-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {/* Main Pages */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Main Pages
                    </h3>
                    {[
                      { path: '/', name: 'Home Page', description: 'Main dashboard and overview' },
                      { path: '/camera', name: 'Camera', description: 'Image capture and upload' },
                      { path: '/history', name: 'History', description: 'Scan history and results' },
                      { path: '/favorites', name: 'Favorites', description: 'User favorite items' },
                      { path: '/analytics', name: 'Analytics', description: 'User analytics and statistics' },
                      { path: '/profile', name: 'Profile', description: 'User profile and information' },
                      { path: '/settings', name: 'Account Settings', description: 'User account settings' }
                    ].map((page) => (
                      <div key={page.path} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <LinkIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{page.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{page.description}</p>
                          <code className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded mt-1 inline-block">
                            {page.path}
                          </code>
                        </div>
                        <a
                          href={page.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                        >
                          Visit →
                        </a>
                      </div>
                    ))}
                  </div>

                  {/* Analysis Pages */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Analysis Pages
                    </h3>
                    {[
                      { path: '/analyze', name: 'Analysis Selection', description: 'Choose analysis provider' },
                      { path: '/analyze/gemini', name: 'Gemini Analysis', description: 'Google Gemini AI analysis' },
                      { path: '/analyze/grok', name: 'Grok Analysis', description: 'X.AI Grok analysis' },
                      { path: '/analyze/serpapi', name: 'SerpAPI Analysis', description: 'Google Search API analysis' },
                      { path: '/analyze/searchapi', name: 'SearchAPI Analysis', description: 'SearchAPI analysis' },
                      { path: '/analyze/decodo', name: 'Decodo Analysis', description: 'Decodo API analysis' },
                      { path: '/analyze/openlens', name: 'OpenLens Analysis', description: 'OpenLens API analysis' }
                    ].map((page) => (
                      <div key={page.path} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <LinkIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{page.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{page.description}</p>
                          <code className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded mt-1 inline-block">
                            {page.path}
                          </code>
                        </div>
                        <a
                          href={page.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                        >
                          Visit →
                        </a>
                      </div>
                    ))}
                  </div>

                  {/* Admin & Demo Pages */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Admin & Demo Pages
                    </h3>
                    {[
                      { path: '/admin', name: 'Admin Panel', description: 'Admin dashboard and management' },
                      { path: '/demo/enhancements', name: 'Image Enhancement Demo', description: 'Image enhancement features' },
                      { path: '/demo/loading', name: 'Loading Demo', description: 'Loading states demonstration' },
                      { path: '/test/database', name: 'Database Test', description: 'Database testing and debugging' },
                      { path: '/live-analysis', name: 'Live Video Analysis', description: 'Real-time video analysis' }
                    ].map((page) => (
                      <div key={page.path} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <LinkIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{page.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{page.description}</p>
                          <code className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded mt-1 inline-block">
                            {page.path}
                          </code>
                        </div>
                        <a
                          href={page.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                        >
                          Visit →
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Auth Pages (for reference) */}
                <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-3">
                    Authentication Pages (Public)
                  </h3>
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {[
                      { path: '/welcome', name: 'Welcome', description: 'Landing page for new users' },
                      { path: '/signin', name: 'Sign In', description: 'User authentication' },
                      { path: '/signup', name: 'Sign Up', description: 'User registration' },
                      { path: '/auth/callback', name: 'Auth Callback', description: 'OAuth callback handler' },
                      { path: '/auth/reset-password', name: 'Reset Password', description: 'Password reset' },
                      { path: '/auth/confirm-email', name: 'Email Confirmation', description: 'Email verification' }
                    ].map((page) => (
                      <div key={page.path} className="text-xs">
                        <span className="font-medium text-yellow-800 dark:text-yellow-200">{page.name}</span>
                        <code className="ml-2 text-yellow-700 dark:text-yellow-300">{page.path}</code>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {selectedUser && (
        <UserProfileModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </RoleGuard>
  );
};

export default AdminPanel;
